import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

// Version string mirrors the pattern used by Voorwaarden's TERMS_VERSION —
// bump when the text materially changes.
export const PRIVACY_VERSION = "v2-2026-09";

export default function Privacybeleid() {
  return (
    <div data-testid="privacybeleid-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Link
        to="/events"
        className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"
        data-testid="privacybeleid-back"
      >
        <ArrowLeft className="h-4 w-4" /> Terug
      </Link>

      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5" /> Versie {PRIVACY_VERSION}
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
        Privacybeleid
      </h1>
      <p className="mt-3 text-slate-300">
        Dit privacybeleid geldt voor het StudioVoorJou ticket-platform: de website waarop je
        tickets koopt, je bestelling terugvindt en (indien van toepassing) inlogt op de
        Companion App van een evenement.
      </p>

      <div className="mt-10 space-y-8 text-slate-300 leading-relaxed">
        <section>
          <h2 className="text-lg font-bold text-white">1. Wie is verantwoordelijk</h2>
          <p className="mt-2">
            StudioVoorJou (KvK 99275112, BTW NL005383438B49) bouwt en beheert dit platform en
            verwerkt daarbij je gegevens namens de organisator van het evenement. De organisator
            bepaalt de inhoud van het event en welke gegevens er nodig zijn om je aanmelding of
            ticket te verwerken (bijvoorbeeld extra registratievelden bij een specifiek
            evenement); StudioVoorJou is verantwoordelijk voor de techniek, opslag en beveiliging
            daaromheen. Vragen over je gegevens kun je altijd stellen via{" "}
            <a href="mailto:info@studiovoorjou.nl" className="text-svj-cyan hover:underline">
              info@studiovoorjou.nl
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">2. Welke gegevens verzamelen we</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Naam en e-mailadres van de koper, om je bestelling en tickets te kunnen leveren.</li>
            <li>
              Naam (en bij Companion-evenementen zoals een Avond4Daagse: route/afstand en
              medaillenummer) van elke deelnemer op een ticket, als een bestelling uit meerdere
              tickets bestaat.
            </li>
            <li>
              Bij evenementen met meerdaagse aanmelding: eventueel adresgegevens en de
              antwoorden op extra registratievelden die de organisator zelf heeft ingesteld
              (bijvoorbeeld een noodcontact of bijrijder).
            </li>
            <li>Je ticketcode/QR-code en scan-/aanwezigheidsgegevens bij het evenement zelf.</li>
            <li>
              Technische gegevens rond je bestelling (ordernummer, tijdstip, betaalstatus) — géén
              kaart- of bankgegevens: die gaan rechtstreeks naar de betaalprovider die voor dit
              evenement wordt gebruikt.
            </li>
          </ul>
          <p className="mt-2 text-slate-400 text-sm">
            Gebruikt de organisator de Companion App (StapKompas) voor zijn evenement? Dan gelden
            daarnaast de aanvullende, functie-specifieke verwerkingen (zoals live navigatie en
            pushmeldingen) die worden toegelicht in de privacyverklaring van de Companion App
            zelf, te vinden in die app onder "Hulp"/instellingen.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">3. Waarvoor gebruiken we deze gegevens</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Om je bestelling te verwerken en je tickets per e-mail te leveren.</li>
            <li>Om de organisator een deelnemerslijst en aanwezigheidsoverzicht te geven.</li>
            <li>Om je te informeren over wijzigingen aan of belangrijk nieuws over het evenement.</li>
            <li>Om te voldoen aan wettelijke (waaronder fiscale) verplichtingen.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">4. Met wie delen we gegevens</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>
              <b>De organisator</b> van het evenement waarvoor je een ticket koopt — die heeft
              een deelnemerslijst nodig om het evenement te kunnen organiseren.
            </li>
            <li>
              <b>De betaalprovider</b> waarmee je bestelling wordt afgerekend — verwerkt je
              betaling rechtstreeks; wij slaan geen kaart- of bankgegevens op.
            </li>
            <li>
              <b>Brevo</b>, voor het versturen van bevestigings- en informatie-e-mails.
            </li>
            <li>
              <b>Supabase</b>, voor de opslag van gegevens in een EU-datacenter (Ierland).
            </li>
          </ul>
          <p className="mt-2">
            We verkopen je gegevens nooit door en gebruiken geen advertentienetwerken of
            trackingcookies op dit platform.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">5. Bewaartermijn</h2>
          <p className="mt-2">
            Bestel- en betaalgegevens bewaren we zo lang als de wet dat voor de fiscale
            bewaarplicht vereist (in Nederland doorgaans 7 jaar). Overige persoonsgegevens
            bewaren we niet langer dan nodig is voor het doel waarvoor ze zijn verzameld — in de
            praktijk doorgaans tot 2 jaar na afloop van het evenement, tenzij we ze eerder mogen
            of moeten verwijderen.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">6. Jouw rechten</h2>
          <p className="mt-2">
            Je hebt recht op inzage, correctie en verwijdering van je gegevens, en op
            gegevensoverdraagbaarheid en bezwaar. Stuur hiervoor een e-mail naar{" "}
            <a href="mailto:info@studiovoorjou.nl" className="text-svj-cyan hover:underline">
              info@studiovoorjou.nl
            </a>
            . We reageren binnen de wettelijke termijn van één maand. Er is geen zelfbedienings-
            knop om je gegevens direct zelf te verwijderen — een verzoek per e-mail behandelen we
            altijd persoonlijk.
          </p>
          <p className="mt-2">
            Je hebt ook het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">7. Beveiliging</h2>
          <p className="mt-2">
            We beveiligen gegevens met versleutelde verbindingen (HTTPS), beperken toegang tot
            geautoriseerd personeel en slaan wachtwoorden nooit leesbaar op.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">8. Cookies</h2>
          <p className="mt-2">
            Dit platform gebruikt geen trackingcookies, advertentiecookies of analytics-scripts
            van derden. We gebruiken alleen technisch noodzakelijke opslag in je browser
            (bijvoorbeeld om je ingelogd te houden) — daarvoor is geen cookietoestemming vereist.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold text-white">9. Wijzigingen</h2>
          <p className="mt-2">
            We kunnen dit privacybeleid aanpassen. De datum hieronder geeft aan wanneer het voor
            het laatst is bijgewerkt. Zie ook onze{" "}
            <Link to="/voorwaarden" className="text-svj-cyan hover:underline">
              algemene voorwaarden
            </Link>
            .
          </p>
        </section>
      </div>

      <div className="mt-14 text-xs text-slate-500">
        Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}
      </div>
    </div>
  );
}
